/**
 * entityDetailPath (P272.1) -- the one place a cascade entity_kind maps
 * to its Twin detail route. Lifted from FindingDetail's local KIND_ROUTE
 * so the cascade impact cards + finding detail share it.
 *
 * D4 moved it from twin/ into shared/: the board names hot spots and
 * has to link them, and shared/ may not import from a feature module.
 * It is a pure route-mapping table with no twin imports, so it belongs
 * on this side of the boundary rather than being duplicated on both.
 *
 * Returns null for kinds with NO detail page (e.g. product) -- callers
 * render plain text instead of a link. The old FindingDetail map linked
 * product to /twin/product/:id, a route that doesn't exist; returning
 * null here fixes that latent dead link.
 */
export const ENTITY_DETAIL_SEGMENT: Record<string, string> = {
  ibs_service: "ibs",
  service: "service",
  capability: "capability",
  vendor: "vendor",
  configuration_item: "ci",
};

export function entityDetailPath(kind: string, id: string): string | null {
  const seg = ENTITY_DETAIL_SEGMENT[kind];
  return seg ? `/twin/${seg}/${id}` : null;
}

/**
 * D3 -- where a supervisory flag's subject lives.
 *
 * The field exam named the connector it was flagging and left the
 * reader to find it: the check knows the row, and the product knows
 * where that row is shown. Some subjects have no detail page of their
 * own but do have a register that opens on them, which is why this is
 * a second map rather than more entries in the first.
 */
const ADMIN_DETAIL_PATH: Record<string, (id: string) => string> = {
  connector: (id) => `/admin/connectors?connector=${id}`,
  outsourcing_contract: () => "/foundation/admin/outsourcing-contracts",
};

export function flagDetailPath(kind: string, id: string): string | null {
  const entity = entityDetailPath(kind, id);
  if (entity) return entity;
  const admin = ADMIN_DETAIL_PATH[kind];
  return admin ? admin(id) : null;
}

/**
 * Round 10 A2 -- where a dependency-layer row leads.
 *
 * The six-layer block names things across six layers, and only three
 * of those kinds have a detail page of their own. The rest have a
 * register that opens on them, and two have neither -- a recovery
 * team and a facility are rows on a service, not places you can go.
 *
 * Null for those, deliberately: `entityDetailPath` established that a
 * link to a route that does not exist is worse than plain text, and
 * that lesson was learned on exactly this shape.
 */
const LAYER_REGISTER_PATH: Record<string, string> = {
  data_asset: "/foundation/admin/data-assets",
  journey: "/twin/journeys",
};

export function layerItemPath(kind: string, id: string): string | null {
  return flagDetailPath(kind, id) ?? LAYER_REGISTER_PATH[kind] ?? null;
}
